/**
 * What the member is told about a write that has not landed yet.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §6.5.
 *
 * WHY A STORE MODULE rather than reactive state inside the engine. `AGENTS.md`
 * puts shared state in Vuex, and this is shared: the Notes Hub, the drawer and
 * the lesson page all show rows the engine holds edits for, and none of them
 * owns the engine. Keeping it here also means a component reads it with
 * `mapState` like everything else, rather than learning a second state system.
 *
 * The engine itself stays free of Vue — `installAutosave` is what joins the two.
 *
 * WHAT THIS IS FOR, and what it is not. It is status, the way a document editor
 * shows "offline": the member did nothing wrong and has nothing to do, because
 * the write is coming back (from commit 7, with retries). It is deliberately NOT
 * a prompt, a gate, or anything that has to be dismissed — decision 9.3 settled
 * that the contract is "notes save themselves", and a control that asks the
 * member to save contradicts it.
 */

const state = () => ({
  /** Keys with an edit the server has not confirmed. `{ [key]: true }`. */
  pending: {},
  /** Keys whose last send failed, and why. `{ [key]: code }`. */
  failed: {},
  /**
   * Keys out of counted attempts (plan §6.2). What the ONE banner in §6.5 is
   * counted from — not one message per entry, and never a toast per failure.
   */
  parked: {},
});

// What `UnsavedNotice` reads. `pending` is kept as state — the engine reports
// it and it is the authority on what is outstanding — but nothing on screen
// reads it: the line shows a FAILED or PARKED row, not one merely in flight.
const getters = {
  /** Did this row's last send fail? The code is for logs, not for the member. */
  failureFor: state => key => state.failed[key] || null,
  isParked: state => key => Boolean(state.parked[key]),
};

const mutations = {
  /**
   * The engine's whole pending set, after every change. Replaced rather than
   * merged: the engine is the authority on what is outstanding.
   *
   * Deliberately does NOT touch `failed`. A slot leaves the pending set the
   * moment its send settles — including when it settled by FAILING — so pruning
   * failures here would erase every one of them on the same tick it was
   * recorded. A failure is cleared by a success (`clearFailed`), which is the
   * only thing that actually means the row is safe.
   */
  setPending(state, keys) {
    const pending = {};
    keys.forEach(key => {
      pending[key] = true;
    });
    state.pending = pending;
  },

  /** The engine's whole parked set, on the same terms as `setPending`. */
  setParked(state, keys) {
    const parked = {};
    keys.forEach(key => {
      parked[key] = true;
    });
    state.parked = parked;
  },

  /** The row reached the server. Nothing left to say about it. */
  clearFailed(state, key) {
    if (!key || !(key in state.failed)) return;
    const { [key]: _cleared, ...rest } = state.failed;
    state.failed = rest;
  },

  setFailed(state, { key, error }) {
    if (!key) return;
    state.failed = {
      ...state.failed,
      [key]: error?.code || error?.response?.status || 'unknown',
    };
  },

  /** Sign-out and the specs. */
  reset(state) {
    state.pending = {};
    state.failed = {};
    state.parked = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
};
